import type { SavedView, SaveViewPayload, SaveViewResult } from "./DataTable.types";

type ApiSuccessEnvelope<T> = {
  success: true;
  status_code: number;
  data: T;
};

type ApiErrorEnvelope = {
  success: false;
  status_code: number;
  message: string;
  error: Record<string, string>;
};

type ApiAuthError = {
  detail: { code: string; message: string };
};

export type SaveViewOptions = {
  /** Base URL of the API service, e.g. `"http://localhost:8000"` or `import.meta.env.VITE_API_BASE_URL`. Trailing slash is optional. */
  baseUrl: string;
  token: string;
  payload: SaveViewPayload;
};

/**
 * POST /api/v1/saveview
 *
 * Saves a user view. Returns the saved view and whether it was a duplicate.
 * Throws on HTTP error (auth, validation, network).
 */
export async function saveView({ baseUrl, token, payload }: SaveViewOptions): Promise<SaveViewResult> {
  const endpoint = `${baseUrl.replace(/\/$/, "")}/api/v1/saveview`;
  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  const body = (await response.json()) as unknown;

  // Auth errors use FastAPI's native shape — not the envelope
  if (response.status === 401) {
    const err = body as ApiAuthError;
    throw new Error(err.detail?.message ?? "Unauthorized");
  }

  if (!response.ok) {
    const err = body as ApiErrorEnvelope;
    throw new Error(err.message ?? `Request failed: ${response.status}`);
  }

  const json = body as ApiSuccessEnvelope<{
    view: SavedView;
    already_exists: boolean;
    message?: string;
  }>;

  return {
    view: json.data.view,
    alreadyExists: json.data.already_exists,
  };
}

/**
 * Creates a pre-configured save function bound to a base URL and token.
 * The returned function matches the `TableSaveView.onSave` signature directly.
 *
 * @example
 * const save = createSaveView({ baseUrl: import.meta.env.VITE_API_BASE_URL, token });
 * <TableSaveView onSave={save} onSaved={...} />
 */
export function createSaveView({ baseUrl, token }: { baseUrl: string; token: string }) {
  return (payload: SaveViewPayload) => saveView({ baseUrl, token, payload });
}
